import { z } from 'zod';
import { postTypesList, type PostTypes } from '$lib/api/restApi';

const tags = ['p', 'h1', 'h2', 'ul', 'ol', 'blockquote', 'pre'] as const;
type Tag = (typeof tags)[number];

export type TextAttributes = {
	datatype: 'block';
	'data-font-size': string;
	'data-alignment': string;
};

// These are constant for all blocks for now. We are currently not supporting
// alignment or font size in the copywriter agent for now.
export const DEFAULT_TEXT_ATTRIBUTES = {
	datatype: 'block',
	'data-font-size': 'normal',
	'data-alignment': 'align-left',
} as const satisfies TextAttributes;

type ContextBlockTypes = Exclude<PostTypes, 'text'>;

export const isValidContextBlockType = (blockType: string): blockType is ContextBlockTypes => {
	if (!blockType) {
		return false;
	}
	if (blockType === 'text') {
		return false;
	}
	return postTypesList.includes(blockType as PostTypes);
};

// V3 schema
const blockSchema = z.discriminatedUnion('type', [
	z.object({
		id: z.string().optional(),
		type: z.literal('paragraph'),
		content: z.string(),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('title'),
		content: z.string(),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('subtitle'),
		content: z.string(),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('orderedList'),
		content: z.array(z.string()),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('unorderedList'),
		content: z.array(z.string()),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('blockquote'),
		content: z.string(),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('codeBlock'),
		content: z.string(),
	}),
	z.object({
		id: z.string().optional(),
		type: z.literal('context'),
		content: z.custom<ContextBlockTypes>(),
	}),
]);
type BlockType = Block['type'];
export type Block = z.infer<typeof blockSchema>;

export const isValidTextBlockType = (
	blockType: string
): blockType is Exclude<BlockType, 'context'> => {
	if (!blockType) {
		return false;
	}
	if (blockType === 'context') {
		return false;
	}
	return blockType in blockTypeToParentTagMap;
};

export const blockTypeToParentTagMap = {
	paragraph: 'p',
	title: 'h1',
	subtitle: 'h2',
	orderedList: 'ol',
	unorderedList: 'ul',
	blockquote: 'blockquote',
	codeBlock: 'pre',
} as const satisfies Record<Exclude<BlockType, 'context'>, Tag>;

export const tagToBlockTypeMap = {
	p: 'paragraph',
	h1: 'title',
	h2: 'subtitle',
	ol: 'orderedList',
	ul: 'unorderedList',
	blockquote: 'blockquote',
	pre: 'codeBlock',
} as const satisfies Record<Tag, Exclude<BlockType, 'context'>>;

export const isValidTagName = (tagName: string): tagName is keyof typeof tagToBlockTypeMap => {
	return tagName in tagToBlockTypeMap;
};

export const aICopywriterPostSchema = z.object({ blocks: z.array(blockSchema).min(1) }).strict();
export type AICopywriterPost = z.infer<typeof aICopywriterPostSchema>;

export type AICopywriterPostChild = {
	id: string;
	type: PostTypes;
	body?: string;
	[key: string]: any;
};
